import React, { useEffect, useState } from "react";
import HeroBanner from "./Hero Banner/hero";
import Cashflow from "./Cashflow/cashflow";
import "./reitsValuation.less";
import service from "../../../services/service";
import { ReitsValuationContextProvider } from "../../../context/reitsValuationContext";
import { useLocation, useParams } from "react-router-dom";
import { normalizeLocation } from "../../../utils/financialHelper";

const reitImages: Record<string, string> = {
  Embassy: new URL("../../../assets/Embassy_1.png", import.meta.url).href,
  Mindspace: new URL("../../../assets/Mindspace_4.png", import.meta.url).href,
  Brookfield: new URL("../../../assets/Brookfield_2.png", import.meta.url).href,
  Nexus: new URL("../../../assets/Nexus_2.png", import.meta.url).href,
};

type ReitLocationItem = {
  location: string;
  properties: string[];
};

const ReitsValuation: React.FC = () => {
  const { id: reitId } = useParams<{ id: string }>();
  const locationState = useLocation();
  const state = locationState.state as { reitId: string; locations: string[] };

  const reitLocations = state?.locations || [];
  const [selectedReit, setSelectedReit] = useState<any | null>(null);

  const [reitSummary, setReitSummary] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchReits = async () => {
      setIsLoading(true);
      try {
        const data = await service.getReits();
        setReitSummary(data);
      } catch (error) {
        console.error("Failed to load REITs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (reitSummary.length === 0) fetchReits();
  }, [reitSummary.length]);

  useEffect(() => {
    if (reitId && reitSummary.length > 0) {
      const found = reitSummary.find((r) => r.id === reitId);
      setSelectedReit(found || null);
    }
  }, [reitId, reitSummary]);

  const reitData = selectedReit
    ? {
        id: selectedReit.id,
        image: reitImages[selectedReit.name.split(" ")[0]], // dynamic image
        name: selectedReit.name,
        locations: selectedReit.location.join(", "),
        assetClass: selectedReit.name.includes("Embassy")
          ? "Commercial Office Space, Hotels, Solar Power Plant"
          : selectedReit.name.includes("Mindspace")
          ? "Commercial Office Space, Business Parks"
          : selectedReit.name.includes("Brookfield")
          ? "Commercial Office Space, Shopping Malls"
          : selectedReit.name.includes("Nexus")
          ? "Shopping Malls, Commercial Office Space, Hotels, Solar Generation Plant"
          : "N/A",

        listingDate: selectedReit.date,
        bse: {
          label: "BSE",
          value: selectedReit.exchanges[0].openPrice,
          change: parseFloat(selectedReit.exchanges[0].openPriceChange),
        },
        nse: {
          label: "NSE",
          value: selectedReit.exchanges[1].openPrice,
          change: parseFloat(selectedReit.exchanges[1].openPriceChange),
        },
        buttons: [],
      }
    : null;

  const [locations, setLocations] = useState<string[]>([]);
  const [propertiesByLocation, setPropertiesByLocation] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    const fetchReitLocationData = async () => {
      if (!reitId) return;

      try {
        const response: ReitLocationItem[] =
          await service.getReitLocationsAndProperties(reitId);

        const locs = response.map((item: ReitLocationItem) => item.location);
        setLocations(locs);

        const map: Record<string, string[]> = {};
        response.forEach((item: ReitLocationItem) => {
          map[item.location] = item.properties;
        });

        setPropertiesByLocation(map);
      } catch (err) {
        console.error("Failed to fetch REIT locations & properties", err);
      }
    };

    fetchReitLocationData();
  }, [reitId]);

  if (isLoading || !reitData) return <div>Loading...</div>;

  return (
    <ReitsValuationContextProvider>
      <div className="reit-valuation">
        <HeroBanner reitData={reitData} />
        <Cashflow
          locations={locations}
          propertiesByLocation={propertiesByLocation}
        />
      </div>
    </ReitsValuationContextProvider>
  );
};

export default ReitsValuation;
